// When may a client write an EMPTY player roster over a populated cloud copy?
//
// The question exists because an empty roster reaches the storage layer from two
// situations that are identical on the wire:
//
//   1. The arbiter removed the last player of a group. A real edit, and it has to
//      reach the cloud — the spectator view is rendered from these documents, so
//      a write that never lands leaves everyone except the arbiter still looking
//      at a player who withdrew.
//
//   2. This client never received the content at all: a co-arbiter who opened the
//      tournament before it loaded, or whose fetch failed. Writing that emptiness
//      would blank out everyone's players for the whole tournament.
//
// Payload size cannot tell them apart, and for a long time the guard refused both
// — which made (2) safe at the cost of making (1) silently impossible.
//
// What separates them is whether this client has ever HELD the document populated
// in this session, from a load or from its own earlier write. The client in (2)
// never has. So an empty payload is treated as an edit when we have held the
// document populated, and as a possible accident otherwise — and only in that
// second case is the remote copy read to check what would be destroyed.
//
// Pure and dependency-free so the table of cases can be tested directly; the
// caller (writeContentDocs in js/services/firebase-auth.js) supplies the facts.

/**
 * Does this write need the remote copy consulted before it is allowed through?
 *
 * False means "write it" — either the payload is not empty, or the emptiness is a
 * deliberate edit. True means "read the remote document first"; the caller then
 * refuses the write only if that document actually has players to lose.
 *
 * @param {object}  facts
 * @param {boolean} facts.mergeWithRemote  the tournament already exists remotely
 *                                         (a brand-new one has nothing to protect)
 * @param {boolean} facts.newIsEmpty       the roster about to be written is empty
 * @param {boolean} facts.heldPopulated    this client has held THIS document with
 *                                         players in it during this session
 * @returns {boolean}
 */
export function mustVerifyBeforeEmptying({ mergeWithRemote, newIsEmpty, heldPopulated }) {
    if (!mergeWithRemote) return false;
    if (!newIsEmpty) return false;
    return !heldPopulated;
}
